import { useNotificationStore } from '../stores/notificationStore';

// Icons for different entity types
const entityIcons = {
  note: 'N',
  activity: 'A',
  contact: 'C',
  contract: 'K',
  plot: 'P',
  proposal: 'R',
  lead: 'L',
  user: 'U',
  crop: 'G',
  invoice: 'I',
  payment: 'M'
};

// Entity type configurations
const entityConfig = {
  note: {
    singular: 'Note',
    path: '/notes'
  },
  activity: {
    singular: 'Activity',
    path: '/activities'
  },
  contact: {
    singular: 'Contact',
    path: '/contacts'
  },
  contract: {
    singular: 'Contract',
    path: '/contracts'
  },
  plot: {
    singular: 'Plot',
    path: '/plots'
  },
  crop: {
    singular: 'Crop',
    path: '/crops'
  },
  proposal: {
    singular: 'Proposal',
    path: '/proposals'
  },
  lead: {
    singular: 'Lead',
    path: '/leads'
  },
  user: {
    singular: 'User',
    path: '/settings'
  },
  invoice: {
    singular: 'Invoice',
    path: '/invoices'
  },
  payment:{
    singular: 'Payment',
    path: '/payments'
  }

};

export type EntityType = keyof typeof entityConfig;

interface EntityData {
  id?: number | string;
  title?: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  username?: string;
  subject?: string;
  description?: string;
}

/**
 * Adds a notification for entity creation
 */
export const notifyEntityCreated = (entityType: EntityType, entityData: EntityData) => {
  const config = entityConfig[entityType];
  const icon = entityIcons[entityType];
  
  // Generate entity name/title
  let entityName = '';
  if (entityData.title) {
    entityName = entityData.title;
  } else if (entityData.name) {
    entityName = entityData.name;
  } else if (entityData.first_name && entityData.last_name) {
    entityName = `${entityData.first_name} ${entityData.last_name}`;
  } else if (entityData.username) {
    entityName = entityData.username;
  } else if (entityData.subject) {
    entityName = entityData.subject;
  } else {
    entityName = `#${entityData.id || 'New'}`;
  }

  // Create notification
  const notification = {
    title: `${icon} New ${config.singular} Created`,
    message: `"${entityName}" has been successfully created.`,
    type: 'success' as const,
    link: entityData.id ? `${config.path}?id=${entityData.id}` : config.path
  };

  // Add to store
  useNotificationStore.getState().addNotification(notification);
};

/**
 * Adds a notification for entity updates
 */
export const notifyEntityUpdated = (entityType: EntityType, entityData: EntityData) => {
  const config = entityConfig[entityType];
  const icon = entityIcons[entityType];
  
  // Generate entity name/title
  let entityName = '';
  if (entityData.title) {
    entityName = entityData.title;
  } else if (entityData.name) {
    entityName = entityData.name;
  } else if (entityData.first_name && entityData.last_name) {
    entityName = `${entityData.first_name} ${entityData.last_name}`;
  } else if (entityData.username) {
    entityName = entityData.username;
  } else {
    entityName = `#${entityData.id}`;
  }

  const notification = {
    title: `${icon} ${config.singular} Updated`,
    message: `"${entityName}" has been successfully updated.`,
    type: 'info' as const,
    link: entityData.id ? `${config.path}?id=${entityData.id}` : config.path
  };

  useNotificationStore.getState().addNotification(notification);
};

/**
 * Adds a notification for successful operations
 */
export const notifySuccess = (title: string, message: string, link?: string) => {
  const notification = {
    title,
    message,
    type: 'success' as const,
    link
  };

  useNotificationStore.getState().addNotification(notification);
};

/**
 * Adds a notification for informational messages
 */
export const notifyInfo = (title: string, message: string, link?: string) => {
  const notification = {
    title: `ℹ️ ${title}`,
    message,
    type: 'info' as const,
    link
  };

  useNotificationStore.getState().addNotification(notification);
};

/**
 * Adds a notification when an entity is deleted
 */
export const notifyEntityDeleted = (entityType: EntityType, entityData: EntityData) => {
  const config = entityConfig[entityType];
  const icon = entityIcons[entityType];
  
  // Generate entity name/title
  let entityName = '';
  if (entityData.title) {
    entityName = entityData.title;
  } else if (entityData.name) {
    entityName = entityData.name;
  } else if (entityData.first_name && entityData.last_name) {
    entityName = `${entityData.first_name} ${entityData.last_name}`;
  } else if (entityData.username) {
    entityName = entityData.username;
  } else {
    entityName = `#${entityData.id || 'Item'}`;
  }

  const notification = {
    title: `${icon} ${config.singular} Deleted`,
    message: `"${entityName}" has been successfully deleted.`,
    type: 'success' as const,
    link: config.path
  };

  useNotificationStore.getState().addNotification(notification);
};

/**
 * Adds a notification when bulk operations complete
 */
export const notifyBulkOperation = (entityType: EntityType, operation: string, count: number) => {
  const config = entityConfig[entityType];
  const icon = entityIcons[entityType];
  
  const notification = {
    title: `${icon} Bulk ${operation} Complete`,
    message: `${count} ${config.singular.toLowerCase()}${count === 1 ? '' : 's'} ${operation.toLowerCase()}.`,
    type: 'success' as const,
    link: config.path
  };

  useNotificationStore.getState().addNotification(notification);
};